import { defineSlotRecipe } from '@chakra-ui/react';

export const sidebarRecipe = defineSlotRecipe({
    slots: ['root', 'panel', 'group', 'item', 'footer'],
    base: {
        root: {
            borderRight: '1px solid',
            borderColor: '{colors.border}',
            // The rail sits ON the shell canvas and must read as one
            // surface with the top bar beside it.
            bg: '{colors.bg.canvas}',
        },
        group: {
            // Section labels match the item text size and weight, set apart
            // one gray step LIGHTER than the full-strength items instead of
            // the old caps + tracking (dark rides gray.300 so the step
            // above the white items stays legible on the chrome). The
            // start inset lines the label up with the item icons' left
            // edge (the icon wells center 16px-in-32px — an 8px inset).
            color: { base: '{colors.fg.muted}', _dark: '#d4d4d8' },
            textStyle: 'body-3',
            ms: 2,
        },
        item: {
            rounded: 'md',
            // Same treatment as the account menu's items: full-strength
            // ink (the lighter gray marks the section labels), hover tint
            // under the pointer. Dark goes full white for maximum
            // legibility on the chrome.
            color: { base: '{colors.fg}', _dark: '{colors.white}' },
            transition: 'background 0.2s ease, color 0.2s ease',
            _hover: {
                bg: '{colors.bg.hover}',
            },
            // data-pending: navigation to this item is in flight (lazy chunk /
            // loader); same highlight as active so a tap gives instant feedback
            // while the sidebar waits for the new page to commit.
            '&[data-active=true], &[data-pending=true]': {
                // Brand-orange selection in both modes: the raw accent on
                // the light panel, one ramp step deeper in dark (less blaze
                // on the dark canvas; white ink clears AA there, 5.4:1).
                bg: { base: '{colors.brand.600}', _dark: '{colors.brand.700}' },
                // The pointer must not restyle the selected row: re-assert
                // the selected fill under :hover, where the combined
                // selector out-ranks the plain hover tint in both modes
                // (declaration order alone proved fragile across the
                // emitted condition selectors).
                _hover: {
                    bg: {
                        base: '{colors.brand.600}',
                        _dark: '{colors.brand.700}',
                    },
                },
                color: '{colors.white}',
                // Lifts the white label off the saturated pill. No preset:
                // Chakra ships no textShadow tokens, and its box-shadow
                // presets embed an `inset` layer that text-shadow can't
                // parse — this is shadows.sm's dark umbra (black 64%) minus
                // that ring.
                textShadow:
                    '0 1px 2px color-mix(in srgb, black 64%, transparent)',
            },
            // Keyboard focus ring. The focusable element is the wrapping
            // router/plain anchor (see sidebar.tsx) — not a Chakra element,
            // so no recipe reaches it and it would show the UA blue ring;
            // sidebar.tsx suppresses that inline, and the themed ring is
            // drawn here on the pill, which carries the rounded shape.
            // colorPalette.focusRing resolves through the default (gray)
            // palette to the brand override in theme.ts. Inset so the
            // panel's overflow clipping can never cut it off.
            'a:focus-visible > &, button:focus-visible > &': {
                outlineWidth: '2px',
                outlineStyle: 'solid',
                outlineColor: 'colorPalette.focusRing',
                outlineOffset: '-2px',
            },
        },
        footer: {
            flexShrink: 0,
            position: 'relative',
            // The dividing line shows only while nav content continues
            // below the footer (data-nav-more-below on the panel, set by
            // Sidebar.Nav), together with the footer's shadow. A pseudo — not a border — so it can OVERHANG
            // the rail edge-to-edge (negative insets below). It fades via
            // color, so appearing never shifts layout.
            _before: {
                content: '""',
                position: 'absolute',
                top: '-1px',
                // Edge to edge across the rail (the flat shell has no
                // gutter to overhang into).
                insetInline: 0,
                height: '1px',
                bg: 'transparent',
                transition: 'background-color 0.2s ease',
            },
            '[data-nav-more-below] > &': {
                _before: { bg: '{colors.border}' },
            },
        },
    },
});
